import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useDashboard, useMetrics, useRetention } from '../api/hooks';
import { FUNNEL_STEPS, REVENUE_SOURCES, type Dashboard, type FunnelStep, type JobState, type Metrics, type RangeDays, type RetentionRow } from '../api/types';
import { PageHeader } from '../app/Shell';
import { REVENUE_SOURCE } from '../app/labels';
import { Badge, BarChart, Button, Card, Count, DataTable, Delta, Empty, Funnel, GroupedBarChart, Icon, Meter, QueryView, Segmented, Stat, fmtInt, fmtTime, fmtUsd, lastUtcDays, type IconName } from '../ui';
import s from './Dashboard.module.css';

const RANGES: ReadonlyArray<{ value: `${RangeDays}`; label: string }> = [
  { value: '1', label: '今日' },
  { value: '7', label: '7 日' },
  { value: '30', label: '30 日' },
];

const FUNNEL_LABEL: Record<FunnelStep, string> = {
  onboarding_completed: '完成新手設定',
  first_story_started: '開始第一個故事',
  story_completed: '完成故事',
  comic_ready: '漫畫完成',
  comic_opened: '打開漫畫',
  purchase_verified: '完成購買',
};

const QUEUE_STATES = ['queued', 'running', 'retry_scheduled'] as const satisfies readonly JobState[];
const QUEUE_LABEL: Record<(typeof QUEUE_STATES)[number], string> = { queued: '排隊中', running: '執行中', retry_scheduled: '等待重試' };

const fmtMoney = (amount: number, currency: string) => new Intl.NumberFormat('zh-HK', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
const fmtHkd = (n: number) => `HK$${fmtInt(Math.round(n))}`;
const fmtAxisUsd = (n: number) => `$${n >= 10 ? Math.round(n) : n.toFixed(n < 1 ? 2 : 1)}`;

export function DashboardPage() {
  const [range, setRange] = useState<RangeDays>(7);
  const dash = useDashboard(range);
  return (
    <>
      <PageHeader
        title="總覽"
        description={dash.dataUpdatedAt ? `更新於 ${fmtTime(dash.dataUpdatedAt)} · 每分鐘自動更新 · 日期以 UTC 計` : '每分鐘自動更新 · 日期以 UTC 計'}
        actions={
          <>
            <Segmented label="時間範圍" value={`${range}`} options={RANGES} onChange={(v) => setRange(Number(v) as RangeDays)} />
            <Button onClick={() => void dash.refetch()} busy={dash.isFetching}>
              <Icon name="refresh" size={16} /> 重新整理
            </Button>
          </>
        }
      />
      <QueryView query={dash} loading="正在計算數據…">
        {(d) => <DashboardBody d={d} />}
      </QueryView>
      <LiveCards />
      <RetentionCard />
    </>
  );
}

function span(range: RangeDays) {
  return range === 1 ? '過去 24 小時' : `過去 ${range} 日`;
}

function DashboardBody({ d }: { d: Dashboard }) {
  const days = lastUtcDays(d.range === 1 ? 2 : d.range);
  const label = (day: string) => day.slice(5).replace('-', '/');
  const active = new Map(d.players.activeByDay.map((r) => [r.day, r.users]));
  const signups = new Map(d.players.signupsByDay.map((r) => [r.day, r]));
  const played = new Map(d.stories.playedByDay.map((r) => [r.day, r.n]));
  const spend = new Map(d.ai.byDay.map((r) => [r.day, r]));
  const share = d.attention.budgetShare;
  const real = (['app_store', 'stripe'] as const).map((k) => d.revenue.bySource[k]).filter((x) => !!x);
  const realHkd = real.reduce((n, x) => n + x.nominalHkd, 0);
  const realPurchases = real.reduce((n, x) => n + x.purchases, 0);
  const p = d.previous;
  const when = span(d.range);

  return (
    <>
      <div className="split">
        <div className={s.kpis}>
          <Stat label="活躍玩家" value={fmtInt(d.players.active)} delta={<Delta now={d.players.active} before={p.active} />} hint={when} />
          <Stat label="新註冊帳戶" value={fmtInt(d.players.signups)} delta={<Delta now={d.players.signups} before={p.signups} />} hint={`另有 ${fmtInt(d.players.guestSignups)} 個訪客`} />
          <Stat
            label="AI 章節 寫成 / 失敗"
            to="/story"
            value={
              <>
                <span className="pos">{fmtInt(d.stories.aiWritten)}</span>
                <span className="faint"> / </span>
                <span className={d.stories.aiFailed ? 'neg' : 'faint'}>{fmtInt(d.stories.aiFailed)}</span>
              </>
            }
            delta={<Delta now={d.stories.aiWritten} before={p.aiWritten} />}
            hint="故事 QA →"
          />
          <Stat label="漫畫 繪製版" to="/manga" value={fmtInt(d.manga.illustrated)} delta={<Delta now={d.manga.illustrated} before={p.illustrated} />} hint={`基本版 ${fmtInt(d.manga.basic)} · 失敗 ${fmtInt(d.manga.failedJobs)} →`} />
          <Stat
            label="今日 AI 花費"
            tone={share >= 0.9 ? 'danger' : share >= 0.7 ? 'warn' : undefined}
            value={fmtUsd(d.ai.todayUsd)}
            delta={
              <Meter
                ariaLabel="今日預算用量"
                parts={[
                  { key: 'used', value: Math.min(d.ai.todayUsd, d.ai.budgetUsd), color: share >= 0.9 ? 'var(--danger)' : 'var(--chart-1)', label: '已用' },
                  { key: 'left', value: Math.max(0, d.ai.budgetUsd - d.ai.todayUsd), color: 'var(--chart-track)', label: '剩餘' },
                ]}
              />
            }
            hint={`每日預算 ${fmtUsd(d.ai.budgetUsd)} 的 ${Math.round(share * 100)}%`}
          />
          <Stat label="收入（定價計）" value={fmtHkd(realHkd)} delta={<Delta now={realHkd} before={p.revenueHkd} />} hint={`${fmtInt(realPurchases)} 筆真實購買`} />
        </div>
        <Attention d={d} />
      </div>

      <div className="grid-2">
        <Card title="每日活躍玩家" sub="有任何 analytics 事件的玩家">
          <BarChart data={days.map((day) => ({ label: label(day), value: active.get(day) ?? 0, title: `${day}：${fmtInt(active.get(day) ?? 0)} 位玩家` }))} format={fmtInt} ariaLabel="每日活躍玩家" />
        </Card>
        <Card title="AI 花費" sub={`${when}合共 ${fmtUsd(d.ai.totalUsd)}`}>
          <GroupedBarChart
            stacked
            data={days.map((day) => ({ label: label(day), title: day, values: { text: spend.get(day)?.textUsd ?? 0, image: spend.get(day)?.imageUsd ?? 0 } }))}
            series={[
              { key: 'text', label: '文字', color: 'var(--chart-1)' },
              { key: 'image', label: '圖像', color: 'var(--chart-2)' },
            ]}
            line={{ value: d.ai.budgetUsd, label: `每日預算 ${fmtUsd(d.ai.budgetUsd)}` }}
            format={fmtUsd}
            axisFormat={fmtAxisUsd}
            ariaLabel="每日 AI 花費"
          />
        </Card>
        <Card title="每日新註冊" sub={`帳戶 ${fmtInt(d.players.signups)} · 訪客 ${fmtInt(d.players.guestSignups)}`}>
          <GroupedBarChart
            data={days.map((day) => ({ label: label(day), title: day, values: { accounts: signups.get(day)?.accounts ?? 0, guests: signups.get(day)?.guests ?? 0 } }))}
            series={[
              { key: 'accounts', label: '帳戶', color: 'var(--chart-1)' },
              { key: 'guests', label: '訪客', color: 'var(--chart-2)' },
            ]}
            format={fmtInt}
            ariaLabel="每日新註冊"
          />
        </Card>
        <Card title="每日已玩章節" sub={`${when}合共 ${fmtInt(d.stories.played)} 章`}>
          <BarChart data={days.map((day) => ({ label: label(day), value: played.get(day) ?? 0, title: `${day}：${fmtInt(played.get(day) ?? 0)} 章` }))} format={fmtInt} ariaLabel="每日已玩章節" color="var(--text-2)" />
        </Card>
      </div>

      <div className="grid-2">
        <Card title="收入來源" sub={`${when} · 已處理的購買`} flush>
          <DataTable
            rows={REVENUE_SOURCES.flatMap((k) => (d.revenue.bySource[k] ? [{ key: k, ...d.revenue.bySource[k]! }] : []))}
            rowKey={(r) => r.key}
            empty="這段時間沒有購買。"
            emptyHint="App 內購及網站付款處理後會在這裏出現。"
            columns={[
              {
                key: 'source',
                header: '來源',
                render: (r) => (
                  <span className={`nowrap ${REVENUE_SOURCE[r.key].test ? 'faint' : ''}`}>
                    {REVENUE_SOURCE[r.key].label}{' '}
                    {REVENUE_SOURCE[r.key].test && (
                      <Badge title="測試付款，不計收入" tone="neutral">
                        測試
                      </Badge>
                    )}
                  </span>
                ),
              },
              { key: 'n', header: '購買', align: 'right', render: (r) => fmtInt(r.purchases) },
              {
                key: 'gross',
                header: '實收',
                align: 'right',
                render: (r) =>
                  Object.keys(r.gross).length ? (
                    Object.entries(r.gross)
                      .map(([c, v]) => fmtMoney(v, c))
                      .join(' + ')
                  ) : (
                    <span className="faint" title="商店事件沒有附上價錢">—</span>
                  ),
              },
              { key: 'nominal', header: '定價 HK$', align: 'right', render: (r) => fmtHkd(r.nominalHkd) },
              { key: 'refunds', header: '退款', align: 'right', render: (r) => (r.refunds ? `${fmtInt(r.refunds)} 筆` : <span className="faint">0</span>) },
            ]}
          />
        </Card>
        <Card title="章節券與產品" sub={when}>
          <div className={s.credits}>
            <div>
              <span className="faint small">發出</span>
              <strong className="num pos">{fmtInt(d.credits.issued)}</strong>
              <Delta now={d.credits.issued} before={p.creditsIssued} />
            </div>
            <div>
              <span className="faint small">消耗</span>
              <strong className="num">{fmtInt(d.credits.spent)}</strong>
              <Delta now={d.credits.spent} before={p.creditsSpent} />
            </div>
            <div>
              <span className="faint small">退款扣回</span>
              <strong className="num">{fmtInt(d.credits.refunded)}</strong>
            </div>
          </div>
          {d.revenue.byProduct.length ? (
            <DataTable
              rows={d.revenue.byProduct}
              rowKey={(r) => r.product}
              initialSort={{ key: 'n', dir: 'desc' }}
              columns={[
                { key: 'product', header: '熱賣產品', render: (r) => <code>{r.product}</code>, sort: (r) => r.product },
                { key: 'n', header: '購買', align: 'right', render: (r) => fmtInt(r.purchases), sort: (r) => r.purchases },
              ]}
            />
          ) : (
            <p className="faint small">這段時間沒有真實購買（測試購買不計）。</p>
          )}
        </Card>
      </div>
    </>
  );
}

/** What needs someone now: each row links to the page that deals with it. */
function Attention({ d }: { d: Dashboard }) {
  const a = d.attention;
  const rows: Array<{ icon: IconName; label: string; n: number; to: string; text?: string }> = [
    { icon: 'alert', label: 'Jobs 永久失敗（24 小時）', n: a.failedJobs, to: '/jobs' },
    { icon: 'flag', label: '待處理檢舉', n: a.openReports, to: '/moderation' },
    { icon: 'robot', label: 'AI 章節失敗（7 日）', n: a.failedChapters, to: '/story?status=failed' },
    { icon: 'book', label: '未過質量檢查（7 日）', n: a.issueChapters, to: '/story?issues=1' },
    { icon: 'coin', label: '今日 AI 預算用量', n: a.budgetShare >= 0.7 ? 1 : 0, to: '/', text: `${Math.round(a.budgetShare * 100)}%` },
  ];
  const open = rows.filter((r) => r.n > 0).length;
  return (
    <Card title="需要處理" sub={open ? `${open} 項` : '一切正常'} className={s.attention}>
      <ul className={s.attnList}>
        {rows.map((r) => (
          <li key={r.label}>
            <Link to={r.to} className={s.attnRow} data-hot={r.n > 0 || undefined}>
              <Icon name={r.icon} size={20} />
              <span className={s.attnLabel}>{r.label}</span>
              {r.text ? <span className={s.attnPct} data-hot={r.n > 0 || undefined}>{r.text}</span> : r.n > 0 ? <Count n={r.n} /> : <Icon name="check" size={18} className={s.ok} />}
              <Icon name="chevron" size={16} />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Live numbers that don't depend on the range: the job queue and the 7-day funnel. */
function LiveCards() {
  const metrics = useMetrics();
  return (
    <QueryView query={metrics} loading="載入即時數據…">
      {(m) => (
        <div className="grid-2">
          <Card title="Jobs 佇列" sub="即時" flush>
            <JobQueue queue={m.jobs.queue} />
          </Card>
          <Card title="轉化漏斗" sub="過去 7 日，按玩家計">
            <FunnelBody m={m} />
          </Card>
        </div>
      )}
    </QueryView>
  );
}

function FunnelBody({ m }: { m: Metrics }) {
  const funnel = FUNNEL_STEPS.map((step) => ({ label: FUNNEL_LABEL[step], value: m.funnel7d[step] ?? 0, title: step }));
  return funnel.some((f) => f.value > 0) ? <Funnel steps={funnel} format={fmtInt} /> : <Empty hint="玩家完成新手設定後就會開始累積。">過去 7 日沒有漏斗事件。</Empty>;
}

function JobQueue({ queue }: { queue: Metrics['jobs']['queue'] }) {
  const types = [...new Set(queue.map((q) => q.type))].sort();
  const count = (type: string, state: JobState) => queue.find((q) => q.type === type && q.state === state)?.count ?? 0;
  const rows = types.map((type) => ({ type, ...Object.fromEntries(QUEUE_STATES.map((st) => [st, count(type, st)])) }) as { type: string } & Record<(typeof QUEUE_STATES)[number], number>);
  return (
    <DataTable
      rows={rows}
      rowKey={(r) => r.type}
      empty="佇列是空的。"
      emptyHint="沒有排隊或執行中的背景工作。"
      columns={[
        { key: 'type', header: '類型', render: (r) => <code>{r.type}</code> },
        ...QUEUE_STATES.map((st) => ({
          key: st,
          header: QUEUE_LABEL[st],
          align: 'right' as const,
          render: (r: (typeof rows)[number]) => <span className={r[st] ? undefined : 'faint'}>{fmtInt(r[st])}</span>,
          sort: (r: (typeof rows)[number]) => r[st],
        })),
      ]}
    />
  );
}

// ---------- retention ----------

const DAY_MS = 86_400_000;
const RETENTION_DAYS = 14;
const RETENTION_SERIES = [
  { key: 'd1', label: 'D1', color: 'var(--chart-1)' },
  { key: 'd7', label: 'D7', color: 'var(--chart-2)' },
] as const;

type Maturity = 'complete' | 'partial' | 'pending';

/**
 * A cohort's Dn is only final once its last joiner could have come back n days later: complete
 * after n+1 whole UTC days, partial during day n (some joiners qualify), pending before.
 */
function maturity(day: string, n: number, now = Date.now()): Maturity {
  const today = Date.parse(new Date(now).toISOString().slice(0, 10));
  const age = Math.round((today - Date.parse(day)) / DAY_MS);
  return age >= n + 1 ? 'complete' : age >= n ? 'partial' : 'pending';
}

const fmtShare = (v: number) => `${Math.round(v * 100)}%`;

function RetentionCell({ value, state }: { value: number; state: Maturity }) {
  if (state === 'pending') return <span className="faint" title="這一批玩家太新，未能計算">—</span>;
  return (
    <span title={state === 'partial' ? '仍在累積：尚有玩家未到這一天' : undefined}>
      {fmtShare(value)}
      {state === 'partial' && <span className="faint">*</span>}
    </span>
  );
}

function RetentionCard() {
  const retention = useRetention();
  return (
    <Card title="留存" sub={`過去 ${RETENTION_DAYS} 個 UTC 日的註冊批次`}>
      <QueryView query={retention} loading="載入留存數據…">
        {(rows) => <RetentionBody rows={rows} />}
      </QueryView>
    </Card>
  );
}

function Figure({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className={s.figure}>
      <span className="faint small">{label}</span>
      <strong className="num">{value}</strong>
      {sub && <span className="faint small">{sub}</span>}
    </div>
  );
}

function RetentionBody({ rows }: { rows: RetentionRow[] }) {
  const byDay = new Map(rows.map((r) => [r.day, r]));
  // Every day of the window, so gaps (no signups) stay visible on the axis.
  const days = lastUtcDays(RETENTION_DAYS);
  const all = days.map((day) => byDay.get(day) ?? { day, joined: 0, d1: 0, d7: 0 });
  const withSignups = all.filter((r) => r.joined > 0);
  if (withSignups.length === 0) return <Empty hint="有新玩家註冊後就會出現。">過去 {RETENTION_DAYS} 日沒有新註冊。</Empty>;

  // Signup-weighted average over cohorts whose value is final.
  const weighted = (n: 1 | 7, pick: (r: RetentionRow) => number) => {
    const done = withSignups.filter((r) => maturity(r.day, n) === 'complete');
    const joined = done.reduce((x, r) => x + r.joined, 0);
    return joined ? { value: done.reduce((x, r) => x + pick(r) * r.joined, 0) / joined, joined } : null;
  };
  const avgD1 = weighted(1, (r) => r.d1);
  const avgD7 = weighted(7, (r) => r.d7);

  const chart = all.map((r) => ({
    label: r.day.slice(5).replace('-', '/'),
    values: {
      d1: r.joined && maturity(r.day, 1) !== 'pending' ? r.d1 : null,
      d7: r.joined && maturity(r.day, 7) !== 'pending' ? r.d7 : null,
    },
    title: r.joined ? `${r.day}：${fmtInt(r.joined)} 人註冊` : `${r.day}：沒有註冊`,
  }));

  return (
    <div className="split">
      <div className="stack">
        <div className="row" style={{ gap: 'var(--sp-6)' }}>
          <Figure label="平均 D1" value={avgD1 ? fmtShare(avgD1.value) : '—'} sub={avgD1 && `${fmtInt(avgD1.joined)} 人`} />
          <Figure label="平均 D7" value={avgD7 ? fmtShare(avgD7.value) : '—'} sub={avgD7 && `${fmtInt(avgD7.joined)} 人`} />
          <span className="faint small" style={{ maxWidth: 360 }}>
            Dn = 註冊後第 n 日或之後仍有活動的比例。* 仍在累積；— 太新未能計算。
          </span>
        </div>
        <GroupedBarChart data={chart} series={RETENTION_SERIES} format={fmtShare} max={1} ariaLabel="每個註冊日的 D1 及 D7 留存" />
      </div>
      <DataTable
        rows={[...withSignups].reverse()}
        rowKey={(r) => r.day}
        pageSize={8}
        columns={[
          { key: 'day', header: '註冊日', render: (r) => <span className="nowrap">{r.day.slice(5)}</span>, sort: (r) => r.day },
          { key: 'joined', header: '人數', align: 'right', render: (r) => fmtInt(r.joined), sort: (r) => r.joined },
          { key: 'd1', header: 'D1', align: 'right', render: (r) => <RetentionCell value={r.d1} state={maturity(r.day, 1)} />, sort: (r) => r.d1 },
          { key: 'd7', header: 'D7', align: 'right', render: (r) => <RetentionCell value={r.d7} state={maturity(r.day, 7)} />, sort: (r) => r.d7 },
        ]}
      />
    </div>
  );
}
